import { useMemo, useState } from "react";
import { Minus, Plus, Calculator } from "lucide-react";

type Item = { name: string; m3: number };
type Category = { name: string; items: Item[] };

export const inventoryCategories: Category[] = [
  {
    name: "Woonkamer",
    items: [
      { name: "Bank 2-zits", m3: 1.2 },
      { name: "Bank 3-zits", m3: 1.8 },
      { name: "Hoekbank", m3: 2.5 },
      { name: "Fauteuil", m3: 0.6 },
      { name: "Salontafel", m3: 0.4 },
      { name: "Tv-meubel", m3: 0.5 },
      { name: "Televisie", m3: 0.3 },
      { name: "Boekenkast", m3: 0.8 },
      { name: "Dressoir / wandkast", m3: 1.2 },
      { name: "Eettafel", m3: 0.8 },
      { name: "Eetkamerstoel", m3: 0.15 },
      { name: "Vloerkleed", m3: 0.2 },
      { name: "Grote plant", m3: 0.25 },
    ],
  },
  {
    name: "Slaapkamer",
    items: [
      { name: "Boxspring / 2-persoonsbed", m3: 2 },
      { name: "1-persoonsbed", m3: 1 },
      { name: "Matras", m3: 0.5 },
      { name: "Kledingkast 2-deurs", m3: 1.4 },
      { name: "Kledingkast 3-deurs of meer", m3: 2.2 },
      { name: "Commode / ladekast", m3: 0.6 },
      { name: "Nachtkastje", m3: 0.2 },
      { name: "Spiegel", m3: 0.2 },
      { name: "Babybed / box", m3: 0.6 },
    ],
  },
  {
    name: "Keuken & witgoed",
    items: [
      { name: "Koelkast", m3: 0.6 },
      { name: "Vrieskast", m3: 0.5 },
      { name: "Amerikaanse koelkast", m3: 1.2 },
      { name: "Wasmachine", m3: 0.5 },
      { name: "Wasdroger", m3: 0.5 },
      { name: "Vaatwasser", m3: 0.4 },
      { name: "Oven / microgolf", m3: 0.15 },
      { name: "Keukenkast / buffet", m3: 1 },
    ],
  },
  {
    name: "Dozen & kleding",
    items: [
      { name: "Verhuisdoos", m3: 0.1 },
      { name: "Garderobedoos (kleding hangend)", m3: 0.3 },
      { name: "Koffer / tas", m3: 0.15 },
    ],
  },
  {
    name: "Kantoor & werkplek",
    items: [
      { name: "Bureau", m3: 0.8 },
      { name: "Bureaustoel", m3: 0.4 },
      { name: "Ladeblok", m3: 0.3 },
      { name: "Archief- / dossierkast", m3: 0.8 },
      { name: "Kantoortafel groot", m3: 1.2 },
    ],
  },
  {
    name: "Berging, schuur & tuin",
    items: [
      { name: "Fiets", m3: 0.5 },
      { name: "Wasdroogrek", m3: 0.2 },
      { name: "Gereedschapskist / kar", m3: 0.3 },
      { name: "Tuinset (tafel + stoelen)", m3: 1.2 },
      { name: "Parasol / partytent", m3: 0.3 },
      { name: "Piano", m3: 1.5 },
      { name: "Kluis / zwaar item", m3: 0.4 },
    ],
  },
];

export type InventoryState = Record<string, number>;

export function inventoryVolume(state: InventoryState) {
  let total = 0;
  for (const category of inventoryCategories) {
    for (const item of category.items) {
      total += (state[item.name] ?? 0) * item.m3;
    }
  }
  return Math.round(total * 10) / 10;
}

/** Veiligheidsmarge in m³ zodat we altijd de juiste wagen inplannen. */
export const INVENTORY_BUFFER_M3 = 5;

/** Netto inboedel + marge: dit is het volume waarop wij de wagen kiezen. */
export function inventoryVolumeAdvised(state: InventoryState) {
  const net = inventoryVolume(state);
  if (net <= 0) return 0;
  return Math.round((net + INVENTORY_BUFFER_M3) * 10) / 10;
}

export function inventoryLines(state: InventoryState) {
  return Object.entries(state)
    .filter(([, qty]) => qty > 0)
    .map(([name, qty]) => `${qty}x ${name}`);
}

export function InventoryCalculator({
  value,
  onChange,
}: {
  value: InventoryState;
  onChange: (next: InventoryState) => void;
}) {
  const [open, setOpen] = useState<string | null>(inventoryCategories[0]?.name ?? null);
  
  const advised = useMemo(() => inventoryVolumeAdvised(value), [value]);
  const chosen = useMemo(() => inventoryLines(value), [value]);

  const setQty = (name: string, qty: number) => {
    const next = { ...value };
    if (qty <= 0) delete next[name];
    else next[name] = qty;
    onChange(next);
  };

  return (
    <div className="grid gap-3 rounded-xl border border-border bg-muted/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-secondary">
            <Calculator className="size-4 text-brand" aria-hidden />
            Inboedel invullen (m³-calculator)
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Vul per ruimte in wat er meegaat. De m³-berekening is een richtlijn op basis van gemiddelde afmetingen.
            Werkelijke afmetingen kunnen per meubel verschillen, vooral bij gedemonteerde items.
          </p>
        </div>
        <div className="rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary">Wij rekenen met</p>
          <p className="font-display text-xl font-extrabold text-brand">± {advised} m³</p>
        </div>
      </div>

      <div className="grid gap-2">
        {inventoryCategories.map((category) => {
          const isOpen = open === category.name;
          const catCount = category.items.reduce((sum, item) => sum + (value[item.name] ?? 0), 0);
          return (
            <div key={category.name} className="overflow-hidden rounded-lg border border-border bg-background">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : category.name)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-semibold text-secondary"
              >
                <span>{category.name}</span>
                <span className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                  {catCount > 0 ? (
                    <span className="rounded-full bg-brand px-2 py-0.5 text-brand-foreground">{catCount}</span>
                  ) : null}
                  {isOpen ? "−" : "+"}
                </span>
              </button>
              {isOpen ? (
                <ul className="grid gap-1 border-t border-border p-2 sm:grid-cols-2">
                  {category.items.map((item) => {
                    const qty = value[item.name] ?? 0;
                    return (
                      <li
                        key={item.name}
                        className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm ${
                          qty > 0 ? "bg-brand/10" : ""
                        }`}
                      >
                        <span className="text-secondary">{item.name}</span>
                        <span className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setQty(item.name, qty - 1)}
                            disabled={qty === 0}
                            aria-label={`Minder ${item.name}`}
                            className="grid size-7 place-items-center rounded-md border border-border text-secondary disabled:opacity-40"
                          >
                            <Minus className="size-3.5" aria-hidden />
                          </button>
                          <span className="w-5 text-center font-semibold text-secondary">{qty}</span>
                          <button
                            type="button"
                            onClick={() => setQty(item.name, qty + 1)}
                            aria-label={`Meer ${item.name}`}
                            className="grid size-7 place-items-center rounded-md border border-border text-secondary hover:border-brand"
                          >
                            <Plus className="size-3.5" aria-hidden />
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>

      {chosen.length ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-secondary">Jouw lijst:</span>
          <span className="text-xs text-muted-foreground">{chosen.join(" · ")}</span>
          <button
            type="button"
            onClick={() => onChange({})}
            className="ml-auto text-xs font-semibold text-brand underline"
          >
            Lijst leegmaken
          </button>
        </div>
      ) : null}
    </div>
  );
}
